import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, PawPrint, Camera, X, Layers, Plus, AlertTriangle, Trash2, Droplets } from 'lucide-react';
import { usePets } from '../../contexts/PetsContext';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../contexts/HydrationGroupsContext';
import { useToast } from '../../contexts/ToastContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { ROUTES } from '../../config/constants';
import { formatGrams } from '../../lib/format';
import { sumDispenserStock } from '../../services/feedingGroupService';

/**
 * Edición de mascota.
 *
 * Campos editables (vía updatePet del service):
 *  - name, species, dailyRationGr, feedingsPerDay, foto.
 *
 * Cambio de grupo (vía movePetToGroup, endpoint dedicado):
 *  - Si el usuario cambia el grupo destino, llamamos movePetToGroup además
 *    de updatePet. Son dos operaciones separadas porque tocan capas distintas
 *    (data del pet vs. relación con feedingGroup) y queremos logs distintos.
 *
 * NO se edita acá: feedingGroupId via updatePet, lastFed (lo maneja feedPet).
 */

const SPECIES = [
  { value: 'dog', label: 'Perro' },
  { value: 'cat', label: 'Gato' },
  { value: 'other', label: 'Otro' },
];

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_DAILY_RATION: 'dailyRationGr',
  INVALID_FEEDINGS_PER_DAY: 'feedingsPerDay',
};

const EditPetPage = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { getPetById, updatePet, movePetToGroup, uploadPetPhoto, deletePetPhoto, softDeletePet, isLoading: petsLoading } = usePets();
  const { groups, isLoading: groupsLoading } = useFeedingGroups();
  const { groups: hydrationGroups } = useHydrationGroups();
  const toast = useToast();

  const pet = getPetById(petId);
  const { url: currentPhotoUrl } = usePetPhoto(pet);

  const [name, setName] = useState('');
  const [species, setSpecies] = useState('dog');
  const [dailyRationGr, setDailyRationGr] = useState('');
  const [feedingsPerDay, setFeedingsPerDay] = useState('2');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [hydrationEnabled, setHydrationEnabled] = useState(false);
  const [selectedHydrationGroupId, setSelectedHydrationGroupId] = useState(null);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Auto-select de grupos/baldes al volver de /groups/new o /water-groups/new.
  // Refs para aplicar UNA SOLA VEZ y no pisar elecciones manuales posteriores
  // cuando llegan nuevos snapshots (mismo patrón que PetForm).
  const searchParams = new URLSearchParams(window.location.search);
  const autoSelectGroupId = searchParams.get('selectGroup');
  const autoSelectWaterGroupId = searchParams.get('selectWaterGroup');
  const autoSelectAppliedRef = useRef(false);
  const autoSelectWaterAppliedRef = useRef(false);
  // Track del id de pet para reinicializar el form solo cuando cambia la
  // mascota subyacente, no en cada snapshot que actualiza algún campo derivado.
  const initializedForPetIdRef = useRef(null);

  // Persistencia del draft entre desmontes (cuando el usuario va a crear un
  // grupo/balde nuevo desde acá). Key incluye petId para no mezclar drafts
  // de distintas mascotas si el usuario abre dos tabs.
  const draftKey = `petcare:editpet.draft.v1.${petId}`;

  useEffect(() => {
    if (!pet) return;
    if (initializedForPetIdRef.current === pet.id) return;
    initializedForPetIdRef.current = pet.id;

    // Si hay un draft (volvimos de crear grupo), priorizamos el draft sobre
    // los valores del pet. Sino, hidratamos desde el pet.
    let draft = null;
    try {
      const raw = sessionStorage.getItem(draftKey);
      draft = raw ? JSON.parse(raw) : null;
    } catch {
      draft = null;
    }

    setName(draft?.name ?? pet.name ?? '');
    setSpecies(draft?.species ?? pet.species ?? 'dog');
    setDailyRationGr(
      draft?.dailyRationGr ?? (pet.dailyRationGr != null ? String(pet.dailyRationGr) : '')
    );
    setFeedingsPerDay(
      draft?.feedingsPerDay ?? (pet.feedingsPerDay != null ? String(pet.feedingsPerDay) : '2')
    );
    setSelectedGroupId(draft?.selectedGroupId ?? pet.feedingGroupId ?? null);
    const initialHydrationId =
      draft && 'selectedHydrationGroupId' in draft
        ? draft.selectedHydrationGroupId
        : pet.hydrationGroupId ?? null;
    setHydrationEnabled(
      typeof draft?.hydrationEnabled === 'boolean'
        ? draft.hydrationEnabled
        : initialHydrationId != null
    );
    setSelectedHydrationGroupId(initialHydrationId);

    if (draft) {
      sessionStorage.removeItem(draftKey);
    }
  }, [pet, draftKey]);

  // Persiste el draft antes de navegar a /groups/new o /water-groups/new.
  const persistDraftAndNavigate = (target) => {
    try {
      sessionStorage.setItem(
        draftKey,
        JSON.stringify({
          name,
          species,
          dailyRationGr,
          feedingsPerDay,
          selectedGroupId,
          hydrationEnabled,
          selectedHydrationGroupId,
        })
      );
    } catch {
      // sessionStorage puede fallar; no es crítico.
    }
    navigate(target);
  };

  // Auto-select del grupo de comida nuevo (si volvimos de /groups/new).
  useEffect(() => {
    if (!autoSelectGroupId || autoSelectAppliedRef.current) return;
    if (groups.some((g) => g.id === autoSelectGroupId)) {
      setSelectedGroupId(autoSelectGroupId);
      autoSelectAppliedRef.current = true;
    }
  }, [autoSelectGroupId, groups]);

  // Auto-select del balde nuevo (si volvimos de /water-groups/new).
  useEffect(() => {
    if (!autoSelectWaterGroupId || autoSelectWaterAppliedRef.current) return;
    if (hydrationGroups.some((g) => g.id === autoSelectWaterGroupId)) {
      setHydrationEnabled(true);
      setSelectedHydrationGroupId(autoSelectWaterGroupId);
      autoSelectWaterAppliedRef.current = true;
    }
  }, [autoSelectWaterGroupId, hydrationGroups]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  if (petsLoading || groupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-600">No encontramos esta mascota.</p>
        </Card>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se aceptan archivos de imagen');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('La imagen supera 15 MB');
      return;
    }
    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    setPhotoRemoved(false);
  };

  const handleRemovePhoto = () => {
    if (photoFile) {
      setPhotoFile(null);
      setPhotoPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } else if (pet.photoExt) {
      setPhotoRemoved(true);
    }
  };

  const dailyRationNum = Number(dailyRationGr);
  const feedingsNum = Number(feedingsPerDay);
  const portionPreview =
    dailyRationNum > 0 && feedingsNum > 0
      ? Math.round(dailyRationNum / feedingsNum)
      : null;

  const groupChanged = selectedGroupId != null && selectedGroupId !== pet.feedingGroupId;
  // Visibilidad: el preview local de la foto recién elegida tiene prioridad;
  // sino, mientras el usuario no la haya marcado para borrar, mostramos la
  // signed URL fresca que devolvió el worker para la foto actual.
  const currentPhotoVisible = !photoRemoved && !photoPreviewUrl && pet.photoExt;
  const displayedPhoto = photoPreviewUrl ?? (currentPhotoVisible ? currentPhotoUrl : null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!selectedGroupId) {
      toast.error('Elegí un grupo');
      return;
    }

    const updates = {};
    if (name.trim() !== pet.name) updates.name = name;
    if (species !== pet.species) updates.species = species;
    if (Number(dailyRationGr) !== pet.dailyRationGr) updates.dailyRationGr = Number(dailyRationGr);
    if (Number(feedingsPerDay) !== pet.feedingsPerDay) updates.feedingsPerDay = Number(feedingsPerDay);

    // Hidratación: comparar contra el valor actual del pet (null si no estaba activa).
    const currentHydrationId = pet.hydrationGroupId ?? null;
    const nextHydrationId = hydrationEnabled ? selectedHydrationGroupId : null;
    if (hydrationEnabled && !selectedHydrationGroupId) {
      toast.error('Elegí un balde o desactivá el tracking de hidratación');
      return;
    }
    if (nextHydrationId !== currentHydrationId) {
      updates.hydrationGroupId = nextHydrationId;
    }

    const hasFieldChanges = Object.keys(updates).length > 0;
    const hasPhotoChange = photoFile != null || photoRemoved;

    if (!hasFieldChanges && !hasPhotoChange && !groupChanged) {
      toast.info('No hay cambios para guardar.');
      return;
    }

    try {
      setSubmitting(true);

      if (hasFieldChanges) {
        await updatePet(petId, updates);
      }

      if (photoFile) {
        try {
          await uploadPetPhoto(petId, photoFile);
        } catch (err) {
          toast.error(err.message || 'No se pudo subir la foto.');
        }
      } else if (photoRemoved && pet.photoExt) {
        try {
          await deletePetPhoto(petId);
        } catch (err) {
          toast.error(err.message || 'No se pudo eliminar la foto.');
        }
      }

      if (groupChanged) {
        await movePetToGroup(petId, selectedGroupId);
      }

      // Cambios guardados → limpiar cualquier draft pendiente del flow
      // "ir a crear grupo y volver".
      sessionStorage.removeItem(draftKey);
      toast.success(`${name.trim()} actualizada.`);
      navigate(`/pets/${petId}`, { replace: true });
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo actualizar la mascota.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await softDeletePet(petId);
      toast.success(`${pet.name} fue eliminada.`);
      // El service hace soft delete (setea deletedAt) y el filter del context
      // la saca de la lista. Volvemos al listado de mascotas.
      navigate(ROUTES.PETS, { replace: true });
    } catch (err) {
      toast.error(err.message || 'No se pudo eliminar la mascota.');
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="self-start"
      >
        Volver
      </Button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Editar mascota</h1>
        <p className="text-sm text-slate-500">Cambios a {pet.name}.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={submitting}
            className={[
              'group relative flex h-24 w-24 items-center justify-center rounded-full',
              'bg-slate-100 ring-2 ring-slate-200 ring-offset-2 ring-offset-slate-50',
              'transition-colors hover:bg-slate-200 disabled:opacity-50',
            ].join(' ')}
            aria-label={displayedPhoto ? 'Cambiar foto' : 'Agregar foto'}
          >
            {displayedPhoto ? (
              <img src={displayedPhoto} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-slate-400">
                <Camera className="h-7 w-7" aria-hidden="true" />
                <span className="text-[10px] font-medium uppercase tracking-wide">Foto</span>
              </div>
            )}
          </button>
          {displayedPhoto && (
            <button
              type="button"
              onClick={handleRemovePhoto}
              disabled={submitting}
              className="relative -ml-6 mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-white shadow-md hover:bg-slate-800 disabled:opacity-50"
              aria-label="Quitar foto"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <Card>
          <div className="flex items-start gap-3 mb-4">
            <div className="rounded-lg bg-brand-100 p-2.5">
              <PawPrint className="h-5 w-5 text-brand-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Datos básicos</h2>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Input
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              maxLength={40}
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Especie
              </label>
              <div className="flex gap-2">
                {SPECIES.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSpecies(s.value)}
                    className={[
                      'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                      species === s.value
                        ? 'border-brand-500 bg-brand-50 text-brand-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="font-semibold text-slate-900 mb-1">Alimentación</h2>
          <p className="text-sm text-slate-500 mb-4">
            Cuánto come por día y en cuántas raciones
          </p>
          <div className="flex flex-col gap-3">
            <Input
              label="Ración diaria total (gramos)"
              type="number"
              inputMode="numeric"
              value={dailyRationGr}
              onChange={(e) => setDailyRationGr(e.target.value)}
              error={errors.dailyRationGr}
              min={5}
              max={5000}
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Comidas por día
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setFeedingsPerDay(String(n))}
                    className={[
                      'flex-1 rounded-xl border px-3 py-2 text-sm font-medium transition-colors',
                      feedingsPerDay === String(n)
                        ? 'border-brand-500 bg-brand-50 text-brand-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {errors.feedingsPerDay && (
                <p className="mt-1.5 text-sm text-status-red">{errors.feedingsPerDay}</p>
              )}
            </div>
            {portionPreview !== null && (
              <div className="rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">
                Cada ración será de aproximadamente <strong>{formatGrams(portionPreview)}</strong>, cada{' '}
                <strong>{Math.round(24 / feedingsNum)} h</strong>.
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3 mb-3">
            <div className="rounded-lg bg-brand-100 p-2.5">
              <Layers className="h-5 w-5 text-brand-700" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Grupo de alimentación</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Cambiar el grupo mueve a la mascota a otro dispenser/bolsa.
              </p>
            </div>
          </div>

          {groupChanged && (
            <div className="mb-3 flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-amber-900">
                Se va a mover a otro grupo. El stock del grupo anterior queda como está
                (no se transfiere). Se registra un evento en la bitácora.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setSelectedGroupId(g.id)}
                className={[
                  'flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors',
                  selectedGroupId === g.id
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-slate-200 bg-white hover:bg-slate-50',
                ].join(' ')}
              >
                <span className="flex-1">
                  <span className="block text-sm font-medium text-slate-900">
                    {g.name}
                    {g.id === pet.feedingGroupId && (
                      <span className="ml-2 text-xs font-normal text-slate-500">(actual)</span>
                    )}
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {(g.dispensers ?? []).length === 0
                      ? `Sin dispenser · Bolsa ${formatGrams(g.mainStockGr)}`
                      : `${(g.dispensers ?? []).length === 1 ? 'Dispenser' : `Dispensers (${g.dispensers.length})`} ${formatGrams(sumDispenserStock(g.dispensers))} · Bolsa ${formatGrams(g.mainStockGr)}`}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={[
                    'ml-2 h-4 w-4 rounded-full shrink-0',
                    selectedGroupId === g.id ? 'bg-brand-600' : 'border-2 border-slate-300',
                  ].join(' ')}
                />
              </button>
            ))}
            <button
              type="button"
              onClick={() =>
                persistDraftAndNavigate(
                  `${ROUTES.GROUP_NEW}?returnTo=${encodeURIComponent(`/pets/${petId}/edit`)}`
                )
              }
              className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors text-left"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Crear grupo nuevo
            </button>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3 mb-3">
            <div className="rounded-lg bg-sky-100 p-2.5">
              <Droplets className="h-5 w-5 text-sky-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-slate-900">Hidratación (opcional)</h2>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={hydrationEnabled}
                    onChange={(e) => setHydrationEnabled(e.target.checked)}
                  />
                  <span className="relative w-10 h-6 bg-slate-200 peer-checked:bg-sky-500 rounded-full transition-colors" aria-hidden="true">
                    <span className={[
                      'absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                      hydrationEnabled ? 'translate-x-4' : '',
                    ].join(' ')} />
                  </span>
                </label>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Trackear cuándo se cambió el agua de {pet.name}.
              </p>
            </div>
          </div>

          {hydrationEnabled && (
            <div className="flex flex-col gap-2 mt-2">
              {hydrationGroups.length === 0 ? (
                <button
                  type="button"
                  onClick={() =>
                    persistDraftAndNavigate(
                      `${ROUTES.WATER_GROUP_NEW}?returnTo=${encodeURIComponent(`/pets/${petId}/edit`)}`
                    )
                  }
                  className="flex items-center gap-2 rounded-xl border border-dashed border-sky-300 px-3 py-3 text-sm text-sky-700 hover:bg-sky-50 transition-colors text-left"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Crear primer balde de agua
                </button>
              ) : (
                <>
                  {hydrationGroups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setSelectedHydrationGroupId(g.id)}
                      className={[
                        'flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors',
                        selectedHydrationGroupId === g.id
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-slate-200 bg-white hover:bg-slate-50',
                      ].join(' ')}
                    >
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {g.name}
                          {g.id === pet.hydrationGroupId && (
                            <span className="ml-2 text-xs font-normal text-slate-500">(actual)</span>
                          )}
                        </span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          Cada {g.hydrationIntervalHours}h
                        </span>
                      </span>
                      <span
                        aria-hidden="true"
                        className={[
                          'ml-2 h-4 w-4 rounded-full shrink-0',
                          selectedHydrationGroupId === g.id ? 'bg-sky-600' : 'border-2 border-slate-300',
                        ].join(' ')}
                      />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      persistDraftAndNavigate(
                        `${ROUTES.WATER_GROUP_NEW}?returnTo=${encodeURIComponent(`/pets/${petId}/edit`)}`
                      )
                    }
                    className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors text-left"
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Crear balde nuevo
                  </button>
                </>
              )}
            </div>
          )}
        </Card>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            fullWidth
            onClick={() => {
              sessionStorage.removeItem(draftKey);
              navigate(`/pets/${petId}`);
            }}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button type="submit" variant="primary" fullWidth isLoading={submitting}>
            Guardar cambios
          </Button>
        </div>

        {/* Zona peligrosa: soft delete. El service marca deletedAt; los logs
            históricos quedan apuntando al petId (integridad referencial). */}
        <Card className="ring-1 ring-red-100 bg-red-50/40">
          <h3 className="font-semibold text-status-red mb-1">Zona peligrosa</h3>
          <p className="text-xs text-slate-600 mb-3">
            Eliminar a {pet.name} la saca del listado y de las próximas
            alimentaciones. El historial de la bitácora se preserva como recuerdo.
          </p>
          <Button
            type="button"
            variant="danger"
            leftIcon={Trash2}
            onClick={() => setConfirmDelete(true)}
            disabled={submitting}
          >
            Eliminar mascota
          </Button>
        </Card>
      </form>

      {/* Modal de confirmación de soft delete */}
      <Modal
        isOpen={confirmDelete}
        onClose={() => !deleting && setConfirmDelete(false)}
        title={`¿Eliminar a ${pet.name}?`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-900">
              Vas a sacar a {pet.name} del hogar. La bitácora histórica se
              mantiene (puedas recordar quién la alimentó, cuándo, etc.) pero
              ya no va a aparecer en la lista ni en las proyecciones.
            </p>
          </div>
          <p className="text-xs text-slate-500">
            No se puede deshacer desde la app (sería necesario soporte técnico
            para restaurarla).
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              fullWidth
              leftIcon={Trash2}
              isLoading={deleting}
              onClick={handleDelete}
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditPetPage;
